import React, {useCallback, useMemo, useState} from "react";

import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faDrawSquare, faLocationDot, faPenLine, faScribble} from "@fortawesome/pro-regular-svg-icons";

import {Trans} from "react-i18next";

import ColorBox from "devextreme-react/cjs/color-box";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";

import {BaseDialogProps} from "@/components/dialogs/base-dialog";

import ClosableDialog from "@/components/dialogs/closable-dialog";
import editMeasureTypeAction
    from "@/pages/takeoff/layouts/measures/layouts/storey-measures/components/dialogs/edit-measure-type-action.ts";
import LoadingButton from "@/components/buttons/loading-button";
import shouldConvertFromCentimetersToMeters
    from "@/pages/takeoff/layouts/measures/layouts/storey-measures/helpers";
import {Case, Switch } from "@/components/switch";

interface EditMeasureTypeFormElements extends HTMLFormControlsCollection {
    name: HTMLInputElement;
}

interface FormElements extends HTMLFormElement { readonly elements: EditMeasureTypeFormElements }

interface EditMeasureTypeProps extends BaseDialogProps {
    measureTypeId: string;
    handleSave?: () => Promise<boolean | void>;
}

const EditMeasureTypeDialog: React.FC<EditMeasureTypeProps> = ({open, onClose, measureTypeId, handleSave}) => {
    const [isPending, setIsPending] = useState(false);

    const { predefinedShapes, getMeasureType, editMeasureTypeLocally } = useProjectMeasureContext();
    
    const measureType = useMemo(() =>  getMeasureType(measureTypeId), [measureTypeId, getMeasureType]);

    const currentShape = useMemo(() => {
        if (measureType == null) return undefined;
        const shapeId = measureType.measureShapeId;
        return predefinedShapes.find(ps => ps.measureShapeId === shapeId);
    }, [predefinedShapes, measureType]);

    const shapeIcon = useMemo(() => {
        if (currentShape == null) return faLocationDot;
        switch (currentShape.name) {
            case 'Point':
                return faLocationDot;
            case 'Line':
                return faPenLine;
            case 'Polyline':
                return faScribble;
            case 'Surface':
                return faDrawSquare;
            default:
                return faLocationDot;
        }
    }, [currentShape])

    const offsetValue = useMemo(() => measureType?.dataInformations?.offset as number | undefined, [measureType?.dataInformations]);
    const colorValue = useMemo(() => {
        const color = measureType?.dataInformations?.color as string | undefined;
        const alpha = measureType?.dataInformations?.alpha as number | undefined;
        return color && alpha ? `${color}${Math.round(alpha * 255).toString(16).padStart(2, '0')}` : undefined;
    }, [measureType?.dataInformations]);

    const [error, setError] = useState<string | undefined>(undefined);

    const resetForm = useCallback(() => {
        setError(undefined);
    }, []);

    const closeDialog = useCallback(() => {
        onClose(false);
        resetForm();
    }, [resetForm, onClose]);

    const handleSubmit = useCallback(async (event: React.FormEvent<FormElements>) => {
        event.preventDefault();
        event.stopPropagation();

        if (measureType == null) return;

        const formData = new FormData(event.currentTarget);

        setIsPending(true);
        setError(undefined);

        try {
            const result = await editMeasureTypeAction({
                measureType,
                formData,
            });

            if (result.success && result.updatedMeasureType) {
                editMeasureTypeLocally(result.updatedMeasureType);
                if (handleSave) {
                    await handleSave();
                }
                closeDialog();
            } else if (result.message) {
                setError(result.message);
            }
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Une erreur s'est produite lors de l'enregistrement.");
        } finally {
            setIsPending(false);
        }
    }, [closeDialog, measureType, editMeasureTypeLocally, handleSave])
    
    const getInputType = (type: unknown): React.HTMLInputTypeAttribute => {
        if (typeof type !== "string") {
            return "text";
        }

        const allowedTypes: React.HTMLInputTypeAttribute[] = ["text", "number"];

        return allowedTypes.includes(type as React.HTMLInputTypeAttribute)
            ? (type as React.HTMLInputTypeAttribute)
            : "text";
    };

    const getInputValue = (key: string): string | number => {
        const value = measureType?.dataInformations?.[key];
        if (value == null) return "";
        if (typeof value !== "number") return String(value);
        return shouldConvertFromCentimetersToMeters(key) ? Math.round(value * 100) : value;
    };

    if(measureType == null)
        return null;

    return (
        <ClosableDialog open={open} onClose={closeDialog} title={`Editer ${measureType.name}`}>
            <form onSubmit={handleSubmit} className='flex flex-col gap-y-2'>
                {error && <div className='bg-red-400 rounded-sm p-2 text-white col-span-2'>{error}</div>}
                
                <div className='flex flex-row gap-x-2 items-center pb-1.5'>
                    <FontAwesomeIcon icon={shapeIcon} className='text-base'/>

                    <span>+ Construction géométrique de type </span>
                    <span>
                        ({''}
                        <Switch
                            value={currentShape?.representations.find(r => r.measureRepresentationId === measureType?.measureRepresentationId)?.name}>
                            <Case value='LineVolume'>
                                <span>Mur</span>
                            </Case>
                            <Case value='LineVolumeOffset'>
                                {offsetValue != null &&
                                    <>
                                        {offsetValue === 0 ? (
                                            <span>Porte</span>
                                        ) : (
                                            <span>Fenêtre</span>
                                        )}
                                    </>
                                }
                            </Case>
                            <Case value='LineVolumeDoor'>
                                <span>Porte</span>
                            </Case>
                            <Case value='Extrusion'>
                                <span>Dalle</span>
                            </Case>
                            <Case value='ExtrusionRoom'>
                                <span>Pièce</span>
                            </Case>
                            <Case value='FineSurface'>
                                <span>Surface</span>
                            </Case>
                            <Case default>
                                <span>Non déterminé</span>
                            </Case>
                        </Switch>
                        {''})
                    </span>
                </div>

                <div className='grid grid-cols-[auto_1fr] items-center gap-y-4 gap-x-4'>
                    <div>Nom</div>
                    <input name="name" type="text" tabIndex={0} autoFocus={true}
                           defaultValue={measureType.name}
                           className="rounded-md px-2 py-0.75 text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"/>

                    <div>Couleur</div>
                    <ColorBox name='color'
                              defaultValue={colorValue ?? '#3B82F6FF'}
                              editAlphaChannel={true} applyButtonText='Appliquer' cancelButtonText='Annuler' height={30}
                              showMaskMode='onFocus' openOnFieldClick={true}/>

                        {measureType?.dataFormatType &&
                            Object.entries(measureType.dataFormatType)
                                .filter(([key]) => key !== "color" && key !== "alpha")
                                .map(([key, type]) => {
                                    if(!(measureType?.dataInformations) || measureType.dataInformations[key] == null) return null;
                                    return (
                                        <React.Fragment key={key}>
                                            <span>
                                                <Trans>{key}</Trans> (<Trans>{key}_unit</Trans>)
                                            </span>

                                            <input name={`dataFormatType_${key}`} type={getInputType(type)}
                                                   defaultValue={getInputValue(key)}
                                                   className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:outline-indigo-600 sm:text-sm/6"
                                            />
                                        </React.Fragment>
                                    );
                                })
                        }
                </div>

                <div className='flex flex-row gap-x-1 items-center pt-2'>
                    <div className='flex text-orange-500 text-sm'>L'ensemble des métrés de ce type seront modifiés sur ce projet.</div>
                    <LoadingButton className='ml-auto' type='submit' isLoading={isPending} body='Valider' />
                </div>
            </form>
        </ClosableDialog> 
    )
}

export default EditMeasureTypeDialog;